import { ColumnDef } from '@tanstack/react-table'
import { ClientDataTable } from 'components/ClientDataTable/ClientDataTable'
import { RouterLink } from 'components/UI/Anchor'
import Importer from 'features/importerv2/Importer'
import { ImporterUpload } from 'features/importerv2/ImporterUpload'
import { ImportPreviewNoData } from 'features/importerv2/importer-types'
import {
  getUnmatchedChainsPreview,
  useGetImportPreviews,
  useDeleteImportPreview,
} from 'features/importerv2/importer-api'
import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import FbButton from 'components/FbUI/FbButton'
import { Modal } from 'components/UI/Modal/Modal'
import { FBPage } from 'components/FbUI/Page/FBPage'
import {
  FBPageHeader,
  FBPageTableContainer,
  FBPageTitle,
  FBPageTitleRow,
} from 'components/FbUI/Page/styles'
import { MergeIcon, PlusIcon } from 'lucide-react'
import { formatDateWithTimeAndMinutes } from 'utils/formatting'
import { ActionButtons } from 'components/Buttons/ActionButtons'
import { PATHS } from 'paths'

export function ImportPreviewsPage() {
  const navigate = useNavigate()
  const { data: importPreviews, isLoading, isFetching } = useGetImportPreviews()
  const [showImportInitModal, setShowImportInitModal] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [importToDelete, setImportToDelete] = useState<number | null>(null)
  const [unmatchedChainsPreviewId, setUnmatchedChainsPreviewId] = useState<
    number | null
  >(null)
  const deleteImportMutation = useDeleteImportPreview()
  const importToDeleteName = useMemo(() => {
    if (!importToDelete) return null
    const importPreview = importPreviews?.find(
      (importPreview) => importPreview.import_number === importToDelete
    )
    return importPreview?.name || 'Unnamed Import'
  }, [importToDelete, importPreviews])

  const columns = useMemo<ColumnDef<ImportPreviewNoData>[]>(() => {
    return [
      {
        header: 'Import Number',
        accessorKey: 'import_number',
      },
      {
        header: 'Name',
        accessorKey: 'name',
        cell: ({ row }) => {
          return (
            <RouterLink
              to={`${PATHS.IMPORT_WIZARD}${row.original.import_number}`}
            >
              {row.original.name ?? 'Unnamed Import'}
            </RouterLink>
          )
        },
      },
      {
        header: 'Created',
        accessorKey: 'created',
        cell: ({ row }) => {
          return formatDateWithTimeAndMinutes(new Date(row.original.created))
        },
      },
      {
        header: 'Modified',
        accessorKey: 'modified',
        cell: ({ row }) => {
          return formatDateWithTimeAndMinutes(new Date(row.original.modified))
        },
      },
      {
        id: 'actions',
        header: 'Actions',
        size: 150,
        meta: {
          isEditable: false,
          headerDisableOrdering: true,
          isLastColumn: true,
          rightAlign: true,
        },
        cell: (info) => {
          return (
            <ActionButtons
              deleteTestId={`button-delete-import-${info.row.original.import_number}`}
              onDelete={() => {
                setImportToDelete(info.row.original.import_number)
                setShowDeleteModal(true)
              }}
            />
          )
        },
      },
    ]
  }, [])

  async function getUnmatchedChainsPreviewId() {
    const response = await getUnmatchedChainsPreview()
    setUnmatchedChainsPreviewId(response.import_number)
  }

  useEffect(() => {
    getUnmatchedChainsPreviewId()
  }, [])

  const handleDeleteImport = async () => {
    if (!importToDelete) return

    try {
      await deleteImportMutation.mutateAsync(importToDelete)
      setShowDeleteModal(false)
      setImportToDelete(null)
    } catch (error) {
      console.error('Failed to delete import:', error)
    }
  }

  return (
    <FBPage>
      <FBPageHeader>
        <FBPageTitleRow>
          <FBPageTitle>Imports</FBPageTitle>
        </FBPageTitleRow>

        <div className="flex flex-row gap-2 justify-end">
          <FbButton onClick={() => setShowImportInitModal(true)}>
            <PlusIcon /> Start New Import
          </FbButton>
          <FbButton
            variant="secondary"
            loading={!unmatchedChainsPreviewId}
            onClick={() => {
              if (unmatchedChainsPreviewId) {
                navigate(`${PATHS.IMPORT_WIZARD}${unmatchedChainsPreviewId}`)
              }
            }}
            disabled={
              unmatchedChainsPreviewId === null ||
              unmatchedChainsPreviewId === -1
            }
          >
            <MergeIcon />
            Merge Unmatched Chains
          </FbButton>
        </div>
      </FBPageHeader>

      <FBPageTableContainer
        style={{
          height: 'calc(var(--base-page-table-height) - 86px)',
        }}
      >
        <ClientDataTable
          loading={isLoading || isFetching}
          height="100%"
          columns={columns}
          data={importPreviews ?? []}
        />
      </FBPageTableContainer>

      <ImporterUpload
        open={showImportInitModal}
        onOpenChange={setShowImportInitModal}
      />

      <Modal
        open={showDeleteModal}
        onOpenChange={setShowDeleteModal}
        title="Delete Import"
        description={`Are you sure you want to delete import "${importToDeleteName}"? This action cannot be undone. All import data and configurations will be permanently removed.`}
        onAccept={handleDeleteImport}
        acceptButtonText="Delete"
        acceptButtonProps={{ variant: 'danger' }}
        cancelButtonText="Cancel"
      />
    </FBPage>
  )
}

export function ImportPreviewPage() {
  const { import_number } = useParams()
  if (!import_number || isNaN(Number(import_number))) {
    window.location.href = PATHS.IMPORT_WIZARD
    return null
  }
  return <Importer importNumber={Number(import_number)} />
}
